/**
 * SystemStatusPage — Administration › System-Status (Story UI-U6 AC9).
 *
 * The HONEST one-row version of the prototype's system-status page: a single
 * "Brokly API" row whose status dot+text comes from GET /health
 * (ok/degraded/down; a fetch failure renders `down`). NO invented services, NO
 * fake latency numbers — a muted note states that a per-service breakdown has no
 * endpoint yet (design.md §2: status = dot + colored text, never a filled pill).
 */

import { StatusDot, type StatusTone } from '../../../components/ui/StatusDot';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useApiHealth } from '../hooks';
import { LoadingState } from '../components';

const meta = ADMIN_PAGE_META['system-status'];

const STATUS_META: Record<string, { tone: StatusTone; label: string }> = {
  ok: { tone: 'success', label: 'Betriebsbereit' },
  degraded: { tone: 'warning', label: 'Eingeschränkt' },
  down: { tone: 'danger', label: 'Nicht erreichbar' },
};

export function SystemStatusPage() {
  const { data, isLoading, isError } = useApiHealth();
  // A fetch failure is itself a signal: render `down` (honest — the API answered
  // nothing). Otherwise take the server's self-reported status.
  const status = isError ? 'down' : (data?.status ?? 'down');
  const m = STATUS_META[status] ?? STATUS_META['down']!;

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />
      {isLoading ? (
        <LoadingState label="Status wird geprüft …" />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold">Brokly API</div>
              <div className="text-xs text-muted-foreground">
                Kern-API (Intake, Policen, Rechnungskontrolle, Courtage).
              </div>
            </div>
            <StatusDot tone={m.tone} label={m.label} />
          </div>
          <p className="text-xs text-muted-foreground">
            Eine Aufschlüsselung nach Einzeldiensten ist noch nicht verfügbar. Es wird der
            aggregierte API-Status angezeigt.
          </p>
        </div>
      )}
    </div>
  );
}
